import { memo, useEffect, useRef, useState } from "react";
import { Divider, Grid2, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Delete, Edit } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";

import Accordion from "../components/Accordion.js";
import { ThirdBackgroundButton } from "../components/Buttons.js";
import Form from "../components/Form.js";
import Spinner from "../components/Spinner.js";
import { useSnackbar } from "../utils/index.js";
import { getTeam, getSubmissions, createSubmission, editSubmission, deleteSubmission } from "../api/index.js";
import Popup from "../components/Popup.js";
import Tooltip from "../components/Tooltip.js";
import Dialog from "../components/Dialog.js";

const useStyles = makeStyles((theme) => ({
	editIcon: {
		color: theme.palette.primary.main,
	},
	deleteIcon: {
		color: theme.palette.error.main,
	},
}));

const Submissions = () => {
	const classes = useStyles();
	const { error, success } = useSnackbar();
	const navigate = useNavigate();
	const [team, setTeam] = useState(null);
	const [submissions, setSubmissions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [addSubmissionPopupOpen, setAddSubmissionPopupOpen] = useState(false);
	const [editSubmissionOpen, setEditSubmissionOpen] = useState(null);
	const [deleteSubmissionOpen, setDeleteSubmissionOpen] = useState(null);

	const formRef = useRef(null);
	const editFormRef = useRef(null);

	const fetchTeam = async () => {
		setIsLoading(true);
		const { success: getSuccess, name: fetchedTeamName, members: fetchedTeamMembers, message } = await getTeam();
		if (getSuccess) {
			setTeam({ name: fetchedTeamName, members: fetchedTeamMembers });
		} else {
			error(message);
		}

		setIsLoading(false);
	};

	const fetchSubmissions = async () => {
		setIsLoading(true);
		const { success: getSuccess, submissions: fetchedSubmissions, message } = await getSubmissions();
		if (getSuccess) {
			setSubmissions(fetchedSubmissions);
		} else {
			error(message);
		}

		setIsLoading(false);
	};

	const onSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: createSuccess, message } = await createSubmission(values);
			if (createSuccess) {
				success(message);
				fetchTeam();
				fetchSubmissions();
			} else {
				error(message);
			}
		} catch {
			error("Something went wrong");
		}

		setAddSubmissionPopupOpen(false);
		setIsLoading(false);
	};

	const onEditSubmit = async (values) => {
		setIsLoading(true);

		try {
			const { success: updateSuccess, message } = await editSubmission(editSubmissionOpen?._id, values);
			if (updateSuccess) {
				success(message);
				fetchTeam();
				fetchSubmissions();
			} else {
				error(message);
			}
		} catch {
			error("Something went wrong");
		}

		setEditSubmissionOpen(null);
		setIsLoading(false);
	};

	const onDeleteSubmit = async () => {
		setIsLoading(true);

		try {
			const { success: deleteSuccess, message } = await deleteSubmission(deleteSubmissionOpen?._id);
			if (deleteSuccess) {
				success(message);
				fetchTeam();
				fetchSubmissions();
			} else {
				error(message);
			}
		} catch {
			error("Something went wrong");
		}

		setDeleteSubmissionOpen(null);
		setIsLoading(false);
	};

	const formContent = [
		{
			customType: "input",
			id: "description",
			type: "text",
			label: "Submission Description",
			placeholder: "Submission Description",
		},
		{
			customType: "input",
			id: "url",
			type: "text",
			label: "Submission Project URL",
			placeholder: "Submission Project URL",
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	const editFormContent = [
		{
			customType: "input",
			id: "description",
			type: "text",
			label: "Submission Description",
			placeholder: "Submission Description",
			value: editSubmissionOpen?.description,
		},
		{
			customType: "input",
			id: "url",
			type: "text",
			label: "Submission Project URL",
			placeholder: "Submission Project URL",
			value: editSubmissionOpen?.url,
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Submit",
			color: "third",
		},
	];

	useEffect(() => {
		fetchTeam();
	}, []);

	useEffect(() => {
		if (team?.name) {
			fetchSubmissions();
		}
	}, [team?.name]);
    
	return (
		<>
			<Spinner open={isLoading} />
			<Grid2 container display="flex" direction="column" justifyContent="center" alignItems="center">
				{!team?.name && (
					<Grid2 display="flex" flexDirection="column" alignItems="center" mt={5}>
						<Typography variant="h4" color="primary" mb={2}>{"No team found"}</Typography>
						<ThirdBackgroundButton onClick={() => navigate("/team")} title="Create Team" />
					</Grid2>
				)}
				{team?.name && (
					<Grid2 display="flex" flexDirection="column" alignItems="center" mt={5}>
						<ThirdBackgroundButton width="300px" onClick={() => setAddSubmissionPopupOpen(true)} title="Create Submission" />
					</Grid2>
				)}
				{team?.name && (
					<Grid2 mt={2} width="100%">
						<Accordion
							alwaysExpanded
							title={(
								<Grid2
									container
									width="100%"
									display="flex"
									direction="row"
									alignItems="center"
									justifyContent="flex-start"
								>
									<Typography>{team.name}</Typography>
								</Grid2>
							)}
							subtitle={(
								<Grid2 container display="flex" direction="row" alignItems="center" justifyContent="center" spacing={0}>
									<Grid2 size={{ xs: 4 }}>
										<Typography>{"Description"}</Typography>
									</Grid2>
									<Grid2 size={{ xs: 7 }} textAlign="center">
										<Typography>{"Project URL"}</Typography>
									</Grid2>
									<Grid2 size={{ xs: 1 }} />
								</Grid2>
							)}
							content={(
								<Grid2 container width="100%" display="flex" direction="column" alignItems="center" justifyContent="center" spacing={0} color="black">
									{submissions.map((sbm, ind) => (
										<Grid2 key={`comp_${ind}`} flexDirection="column" width="100%">
											{ind !== 0
											&& (
												<Grid2 key={`divider_${ind}`} my={1} width="100%">
													<Divider style={{ width: "100%" }} />
												</Grid2>
											)}
											<Grid2 key={`row_${ind}`} container alignItems="center">
												<Grid2 size={{ xs: 4 }}>
													<Typography>{sbm.description}</Typography>
												</Grid2>
												<Grid2 size={{ xs: 7 }} textAlign="center">
													<Link to={sbm.url} target="_blank" rel="noreferrer">{sbm.url}</Link>
												</Grid2>
												<Grid2 size={{ xs: 1 }} display="flex" justifyContent="flex-end">
													<Tooltip title="Edit Submission">
														<IconButton
															onClick={() => setEditSubmissionOpen(sbm)}
														>
															<Edit className={classes.editIcon} />
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete Submission">
														<IconButton
															onClick={() => setDeleteSubmissionOpen(sbm)}
														>
															<Delete className={classes.deleteIcon} />
														</IconButton>
													</Tooltip>
												</Grid2>
											</Grid2>
										</Grid2>
									))}
									{submissions.length === 0 && (
										<Grid2 mt={2}>
											<Typography>{"No submissions found"}</Typography>
										</Grid2>
									)}
								</Grid2>
							)}
						/>
					</Grid2>
				)}
			</Grid2>
			<Popup
				width="400px"
				title="Add Submission"
				open={addSubmissionPopupOpen}
				onClose={() => setAddSubmissionPopupOpen(false)}
			>
				<Form ref={formRef} content={formContent} validationSchema="addSubmission" onSubmit={onSubmit} />
			</Popup>
			<Popup
				width="400px"
				title="Edit Submission"
				open={editSubmissionOpen !== null}
				onClose={() => setEditSubmissionOpen(null)}
			>
				<Form ref={editFormRef} content={editFormContent} validationSchema="addSubmission" onSubmit={onEditSubmit} />
			</Popup>
			<Dialog
				open={deleteSubmissionOpen !== null}
				title="Delete Submission"
				text={"Are you sure you want to delete the submission?"}
				confirmButton="Delete"
				cancelButton="Cancel"
				onConfirm={onDeleteSubmit}
				onClose={() => setDeleteSubmissionOpen(null)}
			/>
		</>
	);
};

export default memo(Submissions);
